import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Layout } from 'antd';
import { useLocalStorageState } from 'ahooks';
import { styled } from 'styled-components';
import { useLocation } from 'react-router-dom';
import { Header as NewAppHeader } from '../../app/shell/Header';
import { SideMenu } from '../../app/shell/SideMenu';
import Header from '../../containers/Header';
import ActionPanel from '../../containers/ActionPanel';
import { OverdueInvoicesNotification } from '../../app/shell/user-action-notifications/OverdueInvoicesNotification';
import { BETA_TOGGLES } from '../../features/beta/screens/Beta';
import { getScreenNameFromRoute } from '../../constants/screen-routes';

const { Content: AntdContent, Header: AntdHeader } = Layout;

export function PageLayout({
  children,
  apiClient,
  trackMethod,
  eventBus,
}: {
  children: JSX.Element;
  apiClient: unknown;
  trackMethod: (
    eventName: string,
    eventPayload?: string | number | object
  ) => void;
  eventBus: unknown;
}) {
  const [showBetaMainMenu] = useLocalStorageState<boolean>(
    BETA_TOGGLES.mainMenu.key,
    {
      listenStorageChange: true,
    }
  );

  const [collapsed, setCollapsed] = useState(false);

  // Persist the last resolved screenName to stabilize initial menu state after refresh
  const [persistedScreenName, setPersistedScreenName] =
    useLocalStorageState<string>('as:lastScreenName', {
      defaultValue: 'calendar',
      listenStorageChange: true,
    });

  let location;
  try {
    // eslint-disable-next-line
    location = useLocation();
  } catch (error) {
    // useLocation will throw if not inside Router context (e.g., in tests)
    location = { hash: '', pathname: '/calendar' };
  }

  const { hash, pathname } = location;
  const prevScreenName = useRef('');

  const screenName = useMemo(() => {
    const routeFragment = (hash || pathname).replace(/^\/#?/, '');
    const fallback = persistedScreenName || 'calendar';
    const currentRoute = routeFragment || fallback;
    const mappedRoute = getScreenNameFromRoute(currentRoute);

    // Only update if the value actually changed
    if (prevScreenName.current !== mappedRoute) {
      prevScreenName.current = mappedRoute;
    }

    return mappedRoute;
  }, [hash, pathname, persistedScreenName]);

  // Persist whenever screenName changes
  useEffect(() => {
    if (screenName && persistedScreenName !== screenName) {
      setPersistedScreenName(screenName);
    }
  }, [screenName, persistedScreenName, setPersistedScreenName]);

  return (
    <Layout className="as-PageLayout" style={{ minHeight: '100vh' }}>
      {showBetaMainMenu ? (
        <>
          <SideMenu collapsed={collapsed} screenName={screenName} />
          <Layout>
            <NewAppHeader
              collapsed={collapsed}
              onCollapseChange={setCollapsed}
            />
            <AntdContent className="as-PageContent">{children}</AntdContent>
          </Layout>
        </>
      ) : (
        <>
          <AntdHeader className="as-PageHeader">
            <ActionPanel />
            <OverdueInvoicesNotification />

            <Header
              {
                // Note: We are using the spread operator to bypass
                // the props ts error, this will be fixed in the next step
                // we are going to use the correct types in new Header component
                ...({
                  apiClient,
                  eventBus,
                  trackMethod,
                } as any)
              }
            />
          </AntdHeader>
          <AntdContent className="as-PageContent">{children}</AntdContent>
        </>
      )}
    </Layout>
  );
}
